import AddIcon from '@mui/icons-material/Add';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIconButton from '../components/DeleteIconButton';
import { Autocomplete, Box, Button, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, IconButton, InputAdornment, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Radio, RadioGroup, Select, Slider, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import TagConditionField from './blacklists/TagConditionField';
import { defaultArgsFor } from './ruleDefaults';
import { effectiveTz, parseDtBound, retzDtBound, serializeDtBound, TZ_LOCAL, TZ_UTC } from './dtRangeBounds';

import { DataAPIContext, GET_LIST } from '../api';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { TOUR_TAG_PICKER_PAGING } from '../components/tourTagPickerPaging';
import RulesetPreview from '../components/RulesetPreview';
import { DateTimePicker, TimePicker } from '../components/TimePicker';
import { ContextStore } from '../store/contextStore';
import { BOOKING_DATETIME_IS_WITHIN_RANGE, BOOKING_IS_REBOOKING, language_iso_codes, PHONE_COUNTRY, REVIEW_IS_POSITIVE_NEGATIVE, REVIEW_RATING, ruleTemplatesOptions, TOUR_PARTICIPANT_FIELD_EXISTS, TOUR_PARTICIPANT_HAS_TAG, TOUR_PARTICIPANT_HAS_TAG_CATEGORY, TOUR_PARTICIPANT_IS_FIELD_AFFECTED, TOUR_PARTICIPANT_IS_UPDATED_VIA, TOUR_PARTICIPANT_SOURCES, TOUR_PARTICIPANT_SURVEY_STATE, TOUR_PARTICIPANT_SURVEY_STATES, TRIP_BEGINS_WITHIN_X_TIME, USER_COUNTRY, USER_ENTERS_LOCATION_RADIUS, USER_HAS_OTHER_TOURS, USER_LANGUAGE, USER_LOGGED_IN, USER_REGISTERED_VIA_OTA, INCLUDE_RULESET, EVENT_TYPE, TPL_PRECHECKIN_STATUS, TPL_EMAIL_RECEIPT, TPL_SETTLEMENT_METHOD, eventOptions, TOUR_HAS_TAG_CATEGORY, OTA_HAS_TAG_CATEGORY, TOUR_OPERATOR_HAS_TAG_CATEGORY, TOUR_HAS_TAG, OTA_HAS_TAG, TOUR_OPERATOR_HAS_TAG, ORDER_HAS_TAG_CATEGORY, ORDER_HAS_TAG, ACCOUNT_HAS_TAG_CATEGORY, ACCOUNT_HAS_TAG, TPL_DELIVERY_METHOD, deliveryMethodsOptions, TPL_CHECKIN_SOURCE, checkinSourceMenuItems, precheckinStatusMenuItems, TPL_TRIP_TIME_RANGE, TPL_HAS_SUBBOOKINGS, DATETIME_WITHIN_RANGE, DATE_WITHIN_RANGE, TIME_WITHIN_RANGE, DT_RANGE_COMPARISONS, settlementMethodMenuItems } from './utils';

// CP-8264: per-flag reference moments for the "User has other tours" rule.
// "now" is the backend default — the rule behaves exactly as before until a
// non-default reference is picked.
const OTHER_TOURS_TIME_REF_OPTIONS = [
    { value: 'now', label: 'Fire time' },
    { value: 'start', label: 'Booking start' },
    { value: 'end', label: 'Booking end' },
];

// args["tour_tags"] ⇄ TagConditionField value for the "User has other tours"
// rule (CP-8264). Chips are stored as {tag: {id}} / {category_code, tag_name};
// the backend reads only the id / the spec strings.
const tourTagsToFieldValue = (tourTags) => {
    const tt = (tourTags && typeof tourTags === 'object') ? tourTags : {};
    const chips = (Array.isArray(tt.tags) ? tt.tags : []).map((c) => {
        if (!c || typeof c !== 'object') { return null; }
        if ('tag' in c) {
            const id = (c.tag && typeof c.tag === 'object') ? c.tag.id : c.tag;
            return Number.isFinite(Number(id)) ? { tag_id: Number(id) } : null;
        }
        if (typeof c.category_code === 'string' && typeof c.tag_name === 'string') {
            return { category_code: c.category_code, tag_name: c.tag_name };
        }
        return null;
    }).filter(Boolean);
    return { chips, match_mode: tt.match_mode === 'or' ? 'or' : 'and', sources: [] };
};

const fieldValueToTourTags = (v) => ({
    match_mode: v.match_mode === 'or' ? 'or' : 'and',
    tags: (v.chips || []).map((c) => (c.tag_id != null
        ? { tag: { id: c.tag_id } }
        : { category_code: c.category_code, tag_name: c.tag_name })),
});

const EMPTY_FIELD_PRESENT = {
    field: '',
    value: '',
    op: 'icontains',
    negate: false,
    has_value: false,
    field_mode: 'path',
    value_mode: 'constant',
};

const NotificationRule = (props) => {
    const dataAPI = React.useContext(DataAPIContext);

    const { countryMap } = React.useContext(ContextStore);

    const listOfCountries = Object.keys(countryMap).map((country) => ({ id: countryMap[country].id, name: countryMap[country].name, code: countryMap[country].country_code }));
    const rule = props.data;
    const addAffectedFieldRef = React.useRef(null);
    const tripBeginsXTimeDeltaRef = React.useRef(null);
    const tripBeginsXTimeUnitRef = React.useRef(null);
    // const [ruleset, setRuleset] = React.useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const disabled = props.disabled

    // React.useEffect(() => {
    //     // console.log('ruleset', rule, ruleset);
    //     if (!rule.nested_ruleset || !rule.nested_ruleset.id) {
    //         setRuleset(null);
    //         return;
    //     }
    //     if (ruleset && (+ruleset.id === +rule.nested_ruleset.id)) { return; }
    //     let active = true;
    //     dataAPI(GET_ONE, 'notification_ruleset', { id: rule.nested_ruleset.id }).then((res) => {
    //         if (!active) { return; }
    //         setRuleset(res.data);
    //     });
    //     return () => { active = false; };
    // });

    const handleChange = (ev, value) => {
        // Create a new rule object with the updated template value. A template
        // switch resets args to that template's defaults (ruleDefaults.js) — keeping
        // the previous template's keys is how range rules got saved without
        // `comparison` and tpl-39 rules without `delta`.
        const tpl = value ? parseInt(value.value) : '';
        const newRule = {
            ...props.data,
            tpl,
            args: tpl === props.data.tpl ? props.data.args : defaultArgsFor(tpl),
        };
        // Call the appropriate update function based on available props
        if (props.onUpdateRule) {
            props.onUpdateRule(newRule);
        } else if (props.setNotification) {
            props.setNotification((prev) => ({
                ...prev,
                rules: (prev.rules || []).map((r) => (r.idx === newRule.idx ? newRule : r)),
            }));
        }
    }

    // Generic handler to update a rule property and call onUpdateRule
    const onRuleChange = (ev) => {
        const { name, value, type, checked } = ev.target;
        const newValue = type === 'checkbox' ? checked : value;

        // Create a new rule object
        const newRule = { ...props.data };

        // Update the specific property
        if (name === 'tpl') {
            newRule[name] = parseInt(newValue);
            if (newRule.tpl !== props.data.tpl) {
                newRule.args = defaultArgsFor(newRule.tpl);
            }
        } else {
            newRule[name] = newValue;
        }

        // Call the appropriate update function based on available props
        if (props.onUpdateRule) {
            props.onUpdateRule(newRule);
        } else if (props.setNotification) {
             props.setNotification((prev) => ({
                ...prev,
                rules: (prev.rules || []).map((r) => (r.idx === newRule.idx ? newRule : r)),
            }));
        }
    };

    const handlePeriodChange = (ev) => {
        // A number of seconds: the backend matcher does timedelta(seconds=period),
        // which raises on a string. Empty = "logged in at all".
        const raw = String(ev.target.value);
        const num_value = raw.trim() === '' || isNaN(+raw) ? null : +raw;
        // console.log('period', num_value, ev.target_value, !isNaN(+ev.target.value));
        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let r = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            r.args = { period: num_value };
            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, r] };
        });
    };

    const handleCountryChange = (ev, countries) => {
        let formatted_country_list = [];
        for (let index = 0; index < countries.length; index++) {
            if (countries[index].code) {
                formatted_country_list.push(countries[index].code);
            } else {
                formatted_country_list.push(countries[index]);
            }
        }
        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let selected_rule = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            selected_rule.args = {};
            selected_rule.args.country = formatted_country_list;

            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, selected_rule] };
        });
    };

    const handleLanguageChange = (ev, languages) => {
        let formatted_lang_list = [];
        for (let index = 0; index < languages.length; index++) {
            if (languages[index].code) {
                formatted_lang_list.push(languages[index].code);
            } else {
                formatted_lang_list.push(languages[index]);
            }
        }

        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let selected_rule = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            selected_rule.args = {};
            if (formatted_lang_list.length === 0) {
                selected_rule.args.lang = null;
            } else {
                selected_rule.args.lang = formatted_lang_list;
            }

            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, selected_rule] };
        });
    };

    //MUI is strict with the type of value for multiple select
    let select_country_value = Array.isArray(rule.args?.country) ? rule.args?.country : rule.args?.country ? [rule.args?.country] : [];
    let select_language_value = Array.isArray(rule.args?.lang) ? rule.args?.lang : rule.args?.lang ? [rule.args?.lang] : [];

    const handleOTAChange = async (ev, ota_list) => {
        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let selected_rule = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!selected_rule.args) {
                selected_rule.args = {};
            }
            selected_rule.args.ota = ota_list;
            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, selected_rule] };
        });
    };


    const [OTAList, setOTAList] = React.useState([]);

    const fetchOTAList = async (nextPage = 1, accumulatedResults = []) => {
        const params = {
            urlSuffix: null,
            filter: {
                page: nextPage,
                page_size: 200,
            },
        };
        let res = await dataAPI(GET_LIST, 'ota', params);

        // Accumulate results, ensuring no duplicates
        const newResults = res.data.filter(newItem =>
            !accumulatedResults.some(existingItem => existingItem.id === newItem.id)
        );

        accumulatedResults = [...accumulatedResults, ...newResults];

        if (res.next) {
            await fetchOTAList(nextPage + 1, accumulatedResults);
        } else {
            setOTAList(accumulatedResults);
        }
    };

    React.useEffect(() => {
        fetchOTAList();
    }, []);

    const onParticipantSourceToggle = (ev, k) => {
        const checkboxEl = ev.target.closest('.MuiListItemButton-root').querySelector('input[type="checkbox"]');
        const is_checked = ev.target === checkboxEl ? checkboxEl.checked : !checkboxEl.checked;
        console.log('onParticipantSourceToggle', k, TOUR_PARTICIPANT_SOURCES[k], checkboxEl, checkboxEl.checked);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            const currentList = r.args && r.args['update_sources'] ? r.args['update_sources'] : [];
            if (is_checked) {
                currentList.push(k);
            } else {
                const idx = currentList.indexOf(k);
                if (idx >= 0) {
                    currentList.splice(idx, 1);
                }
            }
            if (!r.args) {
                r.args = {};
            }
            r.args['update_sources'] = currentList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onParticipantSurveyStateToggle = (ev, k) => {
        const checkboxEl = ev.target.closest('.MuiListItemButton-root').querySelector('input[type="checkbox"]');
        const is_checked = ev.target === checkboxEl ? checkboxEl.checked : !checkboxEl.checked;
        console.log('onParticipantSourceToggle', k, TOUR_PARTICIPANT_SOURCES[k], checkboxEl, checkboxEl.checked);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            const currentList = r.args && r.args['survey_state'] ? r.args['survey_state'] : [];
            if (is_checked) {
                currentList.push(k);
            } else {
                const idx = currentList.indexOf(k);
                if (idx >= 0) {
                    currentList.splice(idx, 1);
                }
            }
            if (!r.args) {
                r.args = {};
            }
            r.args['survey_state'] = currentList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onParticipantRebookingToFromToggle = (ev, k) => {
        const checkboxEl = ev.target.closest('.MuiListItemButton-root').querySelector('input[type="checkbox"]');
        const is_checked = ev.target === checkboxEl ? checkboxEl.checked : !checkboxEl.checked;
        console.log('onParticipantRebookingToFromToggle', k, checkboxEl, checkboxEl.checked, is_checked);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            const current = r.args && r.args['rebooking'] ? r.args['rebooking'] : {};
            current[k] = is_checked;
            if (!r.args) {
                r.args = {};
            }
            r.args['rebooking'] = current;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onAddAffectedField = (ev) => {
        const nf = addAffectedFieldRef.current.value;
        if (!nf || !nf.length) {
            return;
        }
        console.log('onAddAffectedField', addAffectedFieldRef.current, addAffectedFieldRef.current.value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['affected_fields'] ? r.args['affected_fields'] : [];
            if (currentList.indexOf(nf) >= 0) {
                enqueueSnackbar(`Field ${nf} is already on the list`);
                return prev;
            } else {
                currentList.push(nf);
            }
            if (!r.args) {
                r.args = {};
            }
            r.args['affected_fields'] = currentList;
            addAffectedFieldRef.current.value = '';
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onRemoveAffectedField = (ev, rm_idx) => {
        console.log('remove affected fields', ev, rm_idx);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (r.args && r.args['affected_fields']) {
                r.args['affected_fields'] = r.args['affected_fields'].filter((x, idx) => rm_idx !== idx);
            }
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onAddAffectedFieldKeyDown = (ev) => {
        // console.log('key down', ev.keyCode, ev);
        if (ev.keyCode === 13) {
            return onAddAffectedField(ev);
        } else if (ev.keyCode === 27) {
            addAffectedFieldRef.current.value = '';
        }
    };

    const onAddFieldPresent = (ev) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['fields'] ? r.args['fields'] : [];
            currentList.push(EMPTY_FIELD_PRESENT);
            if (!r.args) {
                r.args = {};
            }
            r.args['fields'] = currentList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onFieldExistsRemove = (ev, fidx) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['fields'] ? r.args['fields'] : [];
            if (!r.args) {
                r.args = {};
            }
            r.args['fields'] = currentList.filter((x, xidx) => xidx !== +fidx);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onFieldExistsListSwitch = (ev) => {
        let [fidx, fid] = ev.target.name.split(':');
        fidx = +fidx;
        console.log('add field switch', fidx, fid);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['fields'] ? r.args['fields'] : [];
            const newList = currentList.map((x, xidx) => {
                if (xidx !== fidx) {
                    return x;
                } else {
                    return {
                        ...x,
                        [fid]: !x[fid],
                    };
                }
            });
            if (!r.args) {
                r.args = {};
            }
            r.args['fields'] = newList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onFieldExistsOperatorSwitch = (ev) => {
        let [fidx, fid] = ev.target.name.split(':');
        fidx = +fidx;
        console.log('add field operator', fidx, fid, ev.target.value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['fields'] ? r.args['fields'] : [];
            const newList = currentList.map((x, xidx) => {
                if (xidx !== fidx) {
                    return x;
                } else {
                    return {
                        ...x,
                        [fid]: ev.target.value,
                    };
                }
            });
            if (!r.args) {
                r.args = {};
            }
            r.args['fields'] = newList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }

    const onFieldExistsSwitch = (ev) => {
        console.log('on field exist switch', ev.target);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['match_all'] = ev.target.checked;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onOTASelectSwitch = (ev) => {
        console.log(`on ${ev.target.name} switch: ${ev.target.value}`, ev.target);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args[ev.target.name] = ev.target.value;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onEmailReceiptSelectSwitch = (ev) => {
        console.log('on email receipt switch', ev.target);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['email_receipt'] = ev.target.checked;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }

    const onTripTimeRangeSwitchChange = (ev) => {
        console.log(`on ${ev.target.name} switch: ${ev.target.checked}`, ev.target);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args[ev.target.name] = ev.target.checked;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onTripTimeRangeDeltaChange = (ev) => {
        console.log(`on ${ev.target.name} delta change: ${ev.target.value}`, ev.target);
        // Whole seconds (the field is minutes), like the console. The matcher does
        // int(delta) while the delta's flag is on; a non-number used to be stored as
        // NaN, which reaches the backend as null and raises there.
        const key = ev.target.name;
        const minutes = +ev.target.value;
        const seconds = Number.isFinite(minutes) ? Math.round(minutes * 60) : 0;
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args[key] = seconds;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }

    const onFieldExistsTextUpdate = (ev) => {
        let [fidx, fid] = ev.target.name.split(':');
        fidx = +fidx;
        console.log('add field text update', fidx, fid);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            const currentList = r.args && r.args['fields'] ? r.args['fields'] : [];
            const newList = currentList.map((x, xidx) => {
                if (xidx !== fidx) {
                    return x;
                } else {
                    return {
                        ...x,
                        [fid]: ev.target.value,
                    };
                }
            });
            if (!r.args) {
                r.args = {};
            }
            r.args['fields'] = newList;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onParticipantTagCategorySelect = (ev, cat) => {
        console.log('category selected', cat);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) { r.args = {}; }
            r.args['tag_categories'] = cat;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onParticipantTagSelect = (ev, tags) => {
        console.log('tag selected', tags);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['tags'] = tags;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onTripBeginsWithinXTimeChange = (ev) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r, ev.target.name, ev.target.value);
            if (!r.args) {
                r.args = {};
            }
            r.args['raw_delta'] = ev.target.name === "delta" ? ev.target.value : (r.args.raw_delta || 0);
            r.args['unit'] = ev.target.name === "unit" ? ev.target.value : (r.args.unit || 1);
            r.args['delta'] = Math.round(+r.args['raw_delta'] * +r.args['unit']); // whole seconds: the matcher requires an int
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });

        const raw_delta = tripBeginsXTimeDeltaRef.current?.value;
        const unit = tripBeginsXTimeUnitRef.current?.value;
        console.log('onTripBeginsWithinXTimeChange', raw_delta, unit);
    }

    const onBookingDatetimeWithinXTimeChange = (ev) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r, ev.target.name, ev.target.value);
            if (!r.args) {
                r.args = {};
            }
            r.args['raw_delta'] = ev.target.name === "delta" ? ev.target.value : (r.args.raw_delta || 0);
            r.args['unit'] = ev.target.name === "unit" ? ev.target.value : (r.args.unit || 3600);
            r.args['delta'] = Math.round(+r.args['raw_delta'] * +r.args['unit']); // whole seconds: the matcher requires an int
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });

        const raw_delta = tripBeginsXTimeDeltaRef.current?.value;
        const unit = tripBeginsXTimeUnitRef.current?.value;
        console.log('onBookingDatetimeWithinXTimeChange', raw_delta, unit);
    }

    const onDtRangeArgChange = (key, value) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!r.args) {
                r.args = {};
            }
            r.args[key] = value;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    // Switching the clock keeps the wall clock shown in the pickers and rewrites
    // the stored bounds for the new mode (an offset in UTC mode, none in local).
    const onDtRangeTzChange = (oldTz, newTz) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            const args = r.args || {};
            r.args = {
                ...args,
                tz: newTz,
                from: retzDtBound(r.tpl, oldTz, newTz, args.from),
                to: retzDtBound(r.tpl, oldTz, newTz, args.to),
            };
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onReviewPositiveNegativeSwitch = (ev, key) => {
        console.log('on review positive negative switch', ev.target);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args[key] = ev.target.checked;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onUserHasOtherToursUpdate = (ev, key, value) => {
        console.log('on user has other tours update', ev.target, key, value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            if (key === 'time') {
                if (!r.args[key]) {
                    console.log('overwriting key', key)
                    r.args[key] = {}
                }
                r.args[key][value] = !r.args[key][value];
            } else if (['same_departure', 'same_tour'].indexOf(key) >= 0) {
                console.log('other tours update', key, !r.args[key]);
                r.args[key] = !r.args[key];
            } else if (key === 'tours') {
                if (!r.args[key]) {
                    console.log('overwriting key', key)
                    r.args[key] = []
                }
                console.log('new tours', value);
                r.args[key] = value;
            } else if (key === 'time_ref') {
                // value = [flag, ref] (CP-8264); same lazy-init pattern as "time"
                const [flag, ref] = value;
                if (!r.args[key] || typeof r.args[key] !== 'object') {
                    r.args[key] = {};
                }
                r.args[key][flag] = ref;
            } else if (key === 'tour_tags') {
                r.args[key] = value;
            }
            console.log('r', r);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    };

    const onCheckinSourceSelectSwitch = (ev) => {
        console.log('on checkin source update', ev.target, ev.target.key, ev.target.value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['sources'] = ev.target.value;
            console.log('r', r);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }

    const onPrecheckinStatusSelectSwitch = (ev) => {
        console.log('on precheckin status update', ev.target, ev.target.key, ev.target.value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['precheckin_statuses'] = ev.target.value;
            console.log('r', r);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }

    const onSettlementMethodSelectSwitch = (ev) => {
        console.log('on settlement method update', ev.target, ev.target.key, ev.target.value);
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            console.log('selected rule', r);
            if (!r.args) {
                r.args = {};
            }
            r.args['settlement_method'] = ev.target.value;
            console.log('r', r);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        });
    }
    const onNestedRulesetChange = (ev, ruleset) => {
        props.setNotification((prev) => {
            console.log('nested ruleset change', prev, ruleset);
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            r.nested_ruleset = ruleset;
            console.log('new ruleset', r, ruleset);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        })
    }

    const handleReviewRatingSliderChange = (ev, nv) => {
        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let selected_rule = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!selected_rule.args) {
                selected_rule.args = {};
            }
            selected_rule.args.low = nv[0];
            selected_rule.args.high = nv[1];
            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, selected_rule] };
        });
    };

    const handleUserEntersLocationChange = (ev) => {
        let input_name = ev.target.name;
        let new_value = ev.target.value;

        props.setNotification((prev) => {
            let notification_rules = [...prev.rules];
            let selected_rule = [...notification_rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!selected_rule.args) {
                selected_rule.args = {};
            }
            selected_rule.args[input_name] = new_value;
            notification_rules = notification_rules.filter((ur) => ur.idx !== rule.idx);
            return { ...prev, rules: [...notification_rules, selected_rule] };
        });
    };

    const onEventTypeChange = (ev, nv) => {
        props.setNotification((prev) => {
            console.log('event type change', prev, nv);
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!r.args) {
                r.args = {};
            }
            r.args.events = nv;
            // console.log('new ruleset', r, ruleset);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        })
    }

    const onDeliveryMethodsChange = (ev, nv) => {
        props.setNotification((prev) => {
            console.log('delivery method change', prev, nv);
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!r.args) {
                r.args = {};
            }
            r.args.delivery_methods = nv;
            // Read unguarded by the backend matcher (KeyError when missing).
            if (r.args.allow_undefined === undefined) {
                r.args.allow_undefined = false;
            }
            // console.log('new ruleset', r, ruleset);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        })
    }

    const onAllowUndefinedDeliveryMethodChange = (ev) => {
        props.setNotification((prev) => {
            console.log('allow undefined delivery method change', prev, ev.target.name, ev.target.value);
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!r.args) {
                r.args = {};
            }
            r.args.allow_undefined = !!ev.target.checked;
            // console.log('new ruleset', r, ruleset);
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        })
    }

    const onHasSubbookingsScopeChange = (ev) => {
        props.setNotification((prev) => {
            const r = [...prev.rules.filter((ur) => ur.idx === rule.idx)][0];
            if (!r.args) {
                r.args = {};
            }
            r.args.scope = ev.target.value;
            return {
                ...prev,
                rules: (prev.rules || []).map((ur) => (ur.idx === r.idx ? r : ur)),
            };
        })
    }

    const ruleHasRightPanel = [
        USER_LOGGED_IN, USER_COUNTRY, PHONE_COUNTRY, USER_LANGUAGE, USER_REGISTERED_VIA_OTA, TOUR_PARTICIPANT_IS_UPDATED_VIA,
        TOUR_PARTICIPANT_SURVEY_STATE, TOUR_PARTICIPANT_IS_FIELD_AFFECTED, TOUR_PARTICIPANT_FIELD_EXISTS, BOOKING_IS_REBOOKING,
        TOUR_PARTICIPANT_HAS_TAG_CATEGORY, TOUR_PARTICIPANT_HAS_TAG, TRIP_BEGINS_WITHIN_X_TIME, REVIEW_IS_POSITIVE_NEGATIVE,
        REVIEW_RATING, USER_ENTERS_LOCATION_RADIUS, BOOKING_DATETIME_IS_WITHIN_RANGE, USER_HAS_OTHER_TOURS, INCLUDE_RULESET,
        EVENT_TYPE, TOUR_HAS_TAG_CATEGORY, OTA_HAS_TAG_CATEGORY, TOUR_OPERATOR_HAS_TAG_CATEGORY, TOUR_HAS_TAG, OTA_HAS_TAG, TOUR_OPERATOR_HAS_TAG,
        ORDER_HAS_TAG_CATEGORY, ORDER_HAS_TAG, ACCOUNT_HAS_TAG_CATEGORY, ACCOUNT_HAS_TAG,
        TPL_DELIVERY_METHOD, TPL_CHECKIN_SOURCE, TPL_TRIP_TIME_RANGE, TPL_PRECHECKIN_STATUS, TPL_EMAIL_RECEIPT, TPL_SETTLEMENT_METHOD,
        TPL_HAS_SUBBOOKINGS,
        DATETIME_WITHIN_RANGE, DATE_WITHIN_RANGE, TIME_WITHIN_RANGE,
    ].indexOf(rule.tpl) >= 0;


    return (
        <Grid container spacing={2} justifyContent="flex-start" alignItems="flex-start" alignContent="flex-start" flexWrap={props.isSmallScreen ? "wrap" : "nowrap"}>
            <Grid item xs={12} sm={4} >
                <Autocomplete
                    disabled={disabled}
                    options={ruleTemplatesOptions}
                    getOptionLabel={(option) => option.label}
                    onChange={handleChange}
                    value={ruleTemplatesOptions.find((tpl) => tpl.value === rule.tpl) || null}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Template:"
                            variant="filled"
                            size="small"
                            style={{ minWidth: '300px' }}
                            error={!!(props.issues && props.issues.errors.length)}
                        />
                    )}
                />
                {props.issues && props.issues.errors.map((m) => (
                    <Typography key={`e:${m}`} variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                        {m}
                    </Typography>
                ))}
                {props.issues && !props.issues.errors.length && props.issues.warnings.map((m) => (
                    <Typography key={`w:${m}`} variant="caption" color="warning.main" component="div" sx={{ mt: 0.5 }}>
                        {m} This rule is saved unchanged; editing it will require fixing this.
                    </Typography>
                ))}
            </Grid>

            {rule.tpl === USER_LOGGED_IN && (
                <Grid item xs={12} sm={7} container spacing={2}>
                    <Grid item xs={12} sm={6} >
                        <TextField
                            disabled={disabled}
                            type="number"
                            size="small"
                            variant="filled"
                            fullWidth
                            label="Period:"
                            value={rule.args?.period ?? ''}
                            onChange={handlePeriodChange}
                            />
                    </Grid>
                    {(!+rule.args?.period) && (
                        <Grid item xs={12} sm={6}>
                            <Typography color="#747475" textAlign="justify">
                                If period is not defined, then the rule checks whether the user has logged in at all
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {rule.tpl === USER_COUNTRY && (
                <Grid item xs={12} sm={7} container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disabled={disabled}
                            name="country"
                            fullWidth
                            options={listOfCountries}
                            getOptionLabel={(option) => option?.name || option}
                            size="small"
                            multiple={true}
                            onChange={handleCountryChange}
                            value={select_country_value}
                            renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Country:" />}
                        />
                    </Grid>
                    {(!select_country_value || select_country_value.length === 0) && (
                        <Grid item xs={12} sm={6}>
                            <Typography color="#747475" textAlign="justify">
                                If this argument is not specified, then the rule checks if user's country is not empty
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {rule.tpl === PHONE_COUNTRY && (
                <Grid item xs={12} sm={7} container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disabled={disabled}
                            name="country"
                            fullWidth
                            options={listOfCountries}
                            getOptionLabel={(option) => option?.name || option}
                            size="small"
                            multiple={true}
                            onChange={handleCountryChange}
                            value={select_country_value}
                            renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Country:" />}
                        />
                    </Grid>
                    {(!select_country_value || select_country_value.length === 0) && (
                        <Grid item xs={12} sm={6}>
                            <Typography color="#747475" textAlign="justify">
                                If this argument is not specified, then the rule will always evaluate to false.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {rule.tpl === USER_LANGUAGE && (
                <Grid item xs={12} sm={7} container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disabled={disabled}
                            name="language"
                            fullWidth
                            options={language_iso_codes}
                            getOptionLabel={(option) => option?.name || option}
                            size="small"
                            multiple={true}
                            onChange={handleLanguageChange}
                            value={select_language_value}
                            renderInput={(params) => <TextField {...params} name="language" variant="filled" color="primary" label="Language:" />}
                        />
                    </Grid>
                    {(!select_language_value || select_language_value.length === 0) && (
                        <Grid item xs={12} sm={6} >
                            <Typography color="#747475" textAlign="justify">
                                If this argument is not specified, then the rule checks if user's language is not empty
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {rule.tpl === USER_REGISTERED_VIA_OTA && (
                <Grid item xs={12} sm={7} container direction="row" spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Autocomplete
                            disabled={disabled}
                            style={{ minWidth: '300px' }}
                            options={OTAList}
                            getOptionLabel={(option) => option?.name}
                            size="small"
                            multiple={true}
                            onChange={handleOTAChange}
                            value={rule.args?.ota}
                            renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="OTA:" helperText="Leave empty to match any OTA" />}
                        />
                    </Grid>
                    <Grid item xs={12} container direction="row" spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl disabled={disabled} fullWidth variant="outlined" size="small">
                                <InputLabel>Unique emails</InputLabel>
                                <Select value={rule.args?.unique_emails || 0} name="unique_emails" onChange={onOTASelectSwitch} label="Unique emails">
                                    <MenuItem value={0}>Do not check</MenuItem>
                                    <MenuItem value={1}>Unique emails only</MenuItem>
                                    <MenuItem value={2}>Non-unique emails only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl disabled={disabled} fullWidth variant="outlined" size="small">
                                <InputLabel>Direct/internal</InputLabel>
                                <Select value={rule.args?.direct || 0} name="direct" onChange={onOTASelectSwitch} label="Direct/internal">
                                    <MenuItem value={0}>Do not check</MenuItem>
                                    <MenuItem value={1}>Direct/internal only</MenuItem>
                                    <MenuItem value={2}>Not direct/internal</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl disabled={disabled} fullWidth variant="outlined" size="small">
                                <InputLabel>App promotion</InputLabel>
                                <Select value={rule.args?.app_promotion || 0} name="app_promotion" onChange={onOTASelectSwitch} label="App promotion">
                                    <MenuItem value={0}>Do not check</MenuItem>
                                    <MenuItem value={1}>App promotion allowed</MenuItem>
                                    <MenuItem value={2}>App promotion prohibited</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TOUR_PARTICIPANT_IS_UPDATED_VIA && (
                <Grid item xs={12} sm={7} alignItems="flex-start">
                    <List dense disablePadding>
                        {Object.keys(TOUR_PARTICIPANT_SOURCES).map((k) => (
                            <ListItem dense disablePadding>
                                <ListItemButton data-source={k} role={undefined} onClick={(ev) => onParticipantSourceToggle(ev, k)} dense>
                                    <ListItemIcon>
                                        <Checkbox disabled={disabled} name={`participant_source_${k}`} edge="start" checked={rule.args?.update_sources?.indexOf(k) >= 0} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary={TOUR_PARTICIPANT_SOURCES[k]} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
            )}

            {rule.tpl === TOUR_PARTICIPANT_SURVEY_STATE && (
                <Grid item xs={12} sm={7} alignItems="flex-start">
                    <List dense disablePadding>
                        {Object.keys(TOUR_PARTICIPANT_SURVEY_STATES).map((k) => (
                            <ListItem dense disablePadding>
                                <ListItemButton data-source={k} role={undefined} onClick={(ev) => onParticipantSurveyStateToggle(ev, k)} dense>
                                    <ListItemIcon>
                                        <Checkbox disabled={disabled} name={`participant_survey_state_${k}`} edge="start" checked={rule.args?.survey_state?.indexOf(k) >= 0} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary={TOUR_PARTICIPANT_SURVEY_STATES[k]} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Grid>
            )}

            {rule.tpl === TOUR_PARTICIPANT_IS_FIELD_AFFECTED && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} sm={rule.args?.affected_fields?.length > 0 ? 12 : 6} alignItems="flex-start">
                        {rule.args?.affected_fields?.length > 0 ? (
                            <>
                                <Typography>Custom fields</Typography>
                                <List dense={true}>
                                    {rule.args?.affected_fields?.map((f, idx) => (
                                        <ListItem
                                            secondaryAction={
                                                <DeleteIconButton title="Remove field" size="medium" fontSize="medium" edge="end" aria-label="delete" disabled={disabled} onClick={(ev) => onRemoveAffectedField(ev, idx)} />
                                            }
                                        >
                                            <ListItemText primary={f} />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        ) : (
                            <Box sx={{ marginBottom: '1em' }}>
                                <Typography color="#747475" textAlign="justify">
                                    No custom fields set
                                </Typography>
                            </Box>
                        )}
                        <TextField
                            // label="Field name"
                            disabled={disabled}
                            helperText="Add new field to the list"
                            variant="outlined"
                            size="small"
                            margin="none"
                            fullWidth
                            onKeyDown={onAddAffectedFieldKeyDown}
                            inputRef={addAffectedFieldRef}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Add field">
                                            <IconButton aria-label="add new field" onClick={onAddAffectedField} edge="end">
                                                <AddBoxIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    {!rule.args?.affected_fields || rule.args?.affected_fields?.length <= 0 ? (
                        <Grid item xs={12} sm={6}>
                            <>
                                <Typography color="#747475" textAlign="justify">
                                    The following fields will be checked for changes: first and last names, email, trip start date & time, pickup, number of people in booking
                                </Typography>
                            </>
                        </Grid>
                    ) : null}
                </Grid>
            )}

            {rule.tpl === TOUR_PARTICIPANT_FIELD_EXISTS && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <FormControlLabel disabled={disabled} control={<Switch checked={!!rule.args?.match_all} name="match_all" color="primary" onChange={onFieldExistsSwitch} />} label={!!rule.args?.match_all ? 'Match all fields' : 'Match any field'} />
                    </Grid>
                    {rule.args?.fields?.map((f, fidx, flist) => (
                        <>
                            <Grid item xs={12}>
                                <hr />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControlLabel disabled={disabled} control={<Checkbox checked={f.negate} name={`${fidx}:negate`} color="primary" onChange={onFieldExistsListSwitch} />} label="Not" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField disabled={disabled} variant="outlined" size="small" margin="none" fullWidth name={`${fidx}:field`} value={f.field} label="Field" onChange={onFieldExistsTextUpdate} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl disabled={disabled} fullWidth size="small">
                                    <InputLabel>Field mode</InputLabel>
                                    <Select value={f.field_mode || 'path'} name={`${fidx}:field_mode`} variant='outlined' label="Field mode" onChange={onFieldExistsOperatorSwitch}>
                                        <MenuItem value={"path"}>Path (dotty lookup)</MenuItem>
                                        <MenuItem value={"template"}>Template (DTL)</MenuItem>
                                        <MenuItem value={"constant"}>Constant</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={1}>
                                <DeleteIconButton title="Remove field" size="medium" fontSize="medium" edge="end" aria-label="delete" disabled={disabled} onClick={(ev) => onFieldExistsRemove(ev, fidx)} />
                            </Grid>
                            <Grid item xs={2} sm={2}></Grid>
                            <Grid item xs={5} sm={3} container alignItems="center">
                                <FormControlLabel disabled={disabled} control={<Checkbox checked={f.has_value} name={`${fidx}:has_value`} color="primary" onChange={onFieldExistsListSwitch} />} label="Has value" />
                            </Grid>
                            <Grid item xs={5} sm={5} container alignItems="center">
                                {f.has_value && <>
                                    <FormControl disabled={disabled} fullWidth size="small" label="Comparison operator">
                                        <Select value={f.op} name={`${fidx}:op`} variant='outlined' onChange={onFieldExistsOperatorSwitch}>
                                            <MenuItem value={"icontains"}>Value is a substring of field (case-insensitive)</MenuItem>
                                            <MenuItem value={"contains"}>Value is a substring of field (case-sensitive)</MenuItem>
                                            <MenuItem value={"exact"}>Value matches field exactly (case-sensitive)</MenuItem>
                                            <MenuItem value={"iexact"}>Value matches field exactly (case-insensitive)</MenuItem>
                                            <MenuItem value={"in"}>Field is in bracketed comma-separated list</MenuItem>
                                            <MenuItem value={"gt"}>Field is greater than value (numerical)</MenuItem>
                                            <MenuItem value={"gte"}>Field is greater or equal to value (numerical)</MenuItem>
                                            <MenuItem value={"lt"}>Field is lesser than value (numerical)</MenuItem>
                                            <MenuItem value={"lte"}>Field is lesser or equals to value (numerical)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </>}
                            </Grid>
                            <Grid item xs={0} sm={2}></Grid>
                            {f.has_value && (
                                <>
                                    <Grid item xs={2} sm={2}></Grid>
                                    <Grid item xs={5} sm={5}>
                                        <TextField
                                            disabled={disabled}
                                            variant="outlined"
                                            size="small"
                                            margin="none"
                                            fullWidth
                                            name={`${fidx}:value`}
                                            value={f.value}
                                            label="Value"
                                            onChange={onFieldExistsTextUpdate}
                                        />
                                    </Grid>
                                    <Grid item xs={3} sm={3}>
                                        <FormControl disabled={disabled} fullWidth size="small">
                                            <InputLabel>Value mode</InputLabel>
                                            <Select value={f.value_mode || 'constant'} name={`${fidx}:value_mode`} variant='outlined' label="Value mode" onChange={onFieldExistsOperatorSwitch}>
                                                <MenuItem value={"constant"}>Constant</MenuItem>
                                                <MenuItem value={"path"}>Path (dotty lookup)</MenuItem>
                                                <MenuItem value={"template"}>Template (DTL)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={2} sm={2}></Grid>
                                </>
                            )}
                        </>
                    ))}
                    <Grid item xs={12}>
                        <Button disabled={disabled} variant="outlined" onClick={onAddFieldPresent}>
                            <AddIcon fontSize="small"/>&nbsp;Add field
                        </Button>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === BOOKING_IS_REBOOKING && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <List dense disablePadding>
                        <ListItem dense disablePadding>
                            <ListItemButton disabled={disabled} onClick={(ev) => onParticipantRebookingToFromToggle(ev, 'from')} dense>
                                <ListItemIcon>
                                    <Checkbox name={`participant_rebooking_from`} edge="start" checked={!!rule.args?.rebooking?.from} tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText primary={'New booking'} />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton disabled={disabled} onClick={(ev) => onParticipantRebookingToFromToggle(ev, 'to')} dense>
                                <ListItemIcon>
                                    <Checkbox name={`participant_rebooking_to`} edge="start" checked={!!rule.args?.rebooking?.to} tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText primary={'Old booking'} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Grid>
            )}

            { [TOUR_PARTICIPANT_HAS_TAG_CATEGORY, TOUR_HAS_TAG_CATEGORY, OTA_HAS_TAG_CATEGORY, TOUR_OPERATOR_HAS_TAG_CATEGORY, ORDER_HAS_TAG_CATEGORY, ACCOUNT_HAS_TAG_CATEGORY].indexOf(+rule.tpl) >= 0 && (
                <Grid item xs={12} sm={7} alignItems="flex-start">
                    <RemoteAutocomplete
                        filterSelected
                        disabled={disabled}
                        resource="tour_tag_category"
                        label="Tag categories"
                        multiple
                        variant="filled"
                        size="small"
                        urlSuffix={null}
                        onChange={onParticipantTagCategorySelect}
                        optionsResultsSize={1000}
                        value={rule.args?.tag_categories || []}
                        getOptionLabel={(opt) => (opt && opt.name ? opt.name : opt.toString())}
                        filterOptions={(optlist) => optlist.filter((x) => !rule.args || !rule.args?.tag_categories || rule.args?.tag_categories?.indexOf(x) < 0)}
                        mapResults={(optlist, res) => optlist.map((x) => x.code)}
                    />
                </Grid>
            )}

            { [TOUR_PARTICIPANT_HAS_TAG, TOUR_HAS_TAG, OTA_HAS_TAG, TOUR_OPERATOR_HAS_TAG, ORDER_HAS_TAG, ACCOUNT_HAS_TAG].indexOf(+rule.tpl) >= 0 && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} alignItems="flex-start">
                        <RemoteAutocomplete
                            filterSelected
                            disabled={disabled}
                            resource="tour_tag"
                            label="Tags"
                            multiple
                            variant="filled"
                            size="small"
                            urlSuffix={null}
                            onChange={onParticipantTagSelect}
                            {...TOUR_TAG_PICKER_PAGING}
                            value={rule.args?.tags || []}
                            getOptionLabel={(opt) => {
                                const categoryCode = typeof opt.category === 'string' ? opt.category : (opt.category?.code || '');
                                return `${categoryCode}: ${opt.name}`;
                            }}
                            filterOptions={(optlist) => optlist}
                            mapResults={(optlist, res) => optlist.map((x) => ({ id: x.id, name: x.name, category: x.category.code }))}
                        />
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TRIP_BEGINS_WITHIN_X_TIME && (
                <Grid item xs={12} sm={7} alignItems="center" container spacing={2}>
                    <Grid item xs={12} alignItems="center" container direction="row" spacing={2}>
                        <Grid item xs={12} sm={6} container alignItems="center">
                            <TextField
                                disabled={disabled}
                                // helperText={rule.args?.delta ? `Or ${rule.args?.delta} seconds` : ' '}
                                name="delta"
                                value={rule.args?.raw_delta || ''}
                                variant='filled'
                                size="small"
                                margin="none"
                                fullWidth
                                onChange={onTripBeginsWithinXTimeChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} container alignItems="center">
                            <FormControl disabled={disabled} fullWidth size="small">
                                <Select value={rule.args?.unit || 1} name="unit" variant='filled' label="Unit" onChange={onTripBeginsWithinXTimeChange}>
                                    <MenuItem value={1}>seconds</MenuItem>
                                    <MenuItem value={60}>minutes</MenuItem>
                                    <MenuItem value={3600}>hours</MenuItem>
                                    <MenuItem value={86400}>days</MenuItem>
                                </Select>
                                {/* <FormHelperText>Unit</FormHelperText> */}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {[DATETIME_WITHIN_RANGE, DATE_WITHIN_RANGE, TIME_WITHIN_RANGE].includes(rule.tpl) && (() => {
                const isBetween = (rule.args?.comparison || 'any_between').endsWith('_between');
                const pickerSlotProps = {
                    textField: { variant: 'filled', size: 'small', fullWidth: true },
                    actionBar: { actions: ['clear', 'accept'] },
                };
                const tz = effectiveTz(rule.tpl, rule.args?.tz);
                const tzLabel = tz === TZ_UTC ? 'UTC' : 'tour local time';
                const fromValue = parseDtBound(rule.tpl, tz, rule.args?.from);
                const toValue = parseDtBound(rule.tpl, tz, rule.args?.to);
                const orderInvalid = isBetween && fromValue && toValue && fromValue.isAfter(toValue);
                const renderPicker = (which) => {
                    const value = which === 'from' ? fromValue : toValue;
                    const onChange = (v) => onDtRangeArgChange(which, serializeDtBound(rule.tpl, tz, v));
                    const label = `${which === 'from' ? 'From' : 'To'} (${tzLabel})`;
                    const errorProps = orderInvalid
                        ? { textField: { ...pickerSlotProps.textField, error: true, helperText: 'From must be ≤ To' } }
                        : {};
                    const merged = { ...pickerSlotProps, ...errorProps };
                    if (rule.tpl === DATETIME_WITHIN_RANGE) {
                        // timezone="UTC": show the stored wall clock, not the browser's (see dtRangeBounds.js)
                        return <DateTimePicker disabled={disabled} label={label} value={value} onChange={onChange} slotProps={merged} timezone="UTC" />;
                    }
                    if (rule.tpl === DATE_WITHIN_RANGE) {
                        return <DatePicker disabled={disabled} label={label} value={value} onChange={onChange} format="YYYY-MM-DD" slotProps={merged} />;
                    }
                    return <TimePicker disabled={disabled} label={label} value={value} onChange={onChange} slotProps={merged} />;
                };
                return (
                    <Grid item xs={12} sm={7} container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                disabled={disabled}
                                variant="filled"
                                size="small"
                                fullWidth
                                label="Input path"
                                value={rule.args?.input || ''}
                                onChange={(ev) => onDtRangeArgChange('input', ev.target.value)}
                                helperText={'e.g. "participant.start_ts" or "order.bookings.:.start_ts" (\':\' iterates lists)'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl disabled={disabled} fullWidth size="small" error={!rule.args?.comparison}>
                                <InputLabel>Comparison</InputLabel>
                                {/* No display-only default: a stored rule without `comparison` never
                                    matches (the ruleset-2495 incident), so it shows as an empty, invalid
                                    choice. New rules get `any_between` written by the template defaults. */}
                                <Select
                                    label="Comparison"
                                    variant="filled"
                                    value={rule.args?.comparison || ''}
                                    onChange={(ev) => onDtRangeArgChange('comparison', ev.target.value)}
                                >
                                    {DT_RANGE_COMPARISONS.map((c) => (
                                        <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {rule.args?.comparison
                                        ? '"any" and "all" behave the same on single values'
                                        : 'Choose how the values are compared — without one the rule never matches'}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl disabled={disabled} fullWidth size="small">
                                <InputLabel>Times are in</InputLabel>
                                <Select
                                    label="Times are in"
                                    variant="filled"
                                    value={tz}
                                    onChange={(ev) => onDtRangeTzChange(tz, ev.target.value)}
                                >
                                    <MenuItem value={TZ_UTC}>UTC</MenuItem>
                                    <MenuItem value={TZ_LOCAL}>Tour local time</MenuItem>
                                </Select>
                                <FormHelperText>
                                    {tz === TZ_UTC
                                        ? 'Values are converted to UTC before they are compared.'
                                        : "Values are compared on their own clock: the tour's time zone for tour times, UTC for system timestamps."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={isBetween ? 6 : 12}>
                            {renderPicker('from')}
                        </Grid>
                        {isBetween && (
                            <Grid item xs={12} sm={6}>
                                {renderPicker('to')}
                            </Grid>
                        )}
                    </Grid>
                );
            })()}

            {rule.tpl === REVIEW_IS_POSITIVE_NEGATIVE && (
                <Grid item xs={12} sm={7} alignItems="center" container spacing={2}>
                    <Grid item xs={12}>
                        <List dense disablePadding>
                            <ListItem dense disablePadding key="no_review">
                                <ListItemButton disabled={disabled} onClick={(ev) => onReviewPositiveNegativeSwitch(ev, "no_review")} dense>
                                    <ListItemIcon>
                                        <Checkbox name="no_review" edge="start" checked={!!rule.args?.no_review} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary="No review" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem dense disablePadding key="positive_review">
                                <ListItemButton disabled={disabled} onClick={(ev) => onReviewPositiveNegativeSwitch(ev, "positive_review")} dense>
                                    <ListItemIcon>
                                        <Checkbox name="positive_review" edge="start" checked={!!rule.args?.positive_review} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary="Positive review" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem dense disablePadding key="negative_review">
                                <ListItemButton disabled={disabled} onClick={(ev) => onReviewPositiveNegativeSwitch(ev, "negative_review")} dense>
                                    <ListItemIcon>
                                        <Checkbox name="negative_review" edge="start" checked={!!rule.args?.negative_review} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary="Negative review" />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === REVIEW_RATING && (
                <Grid item xs={12} sm={7} alignItems="center" container spacing={2}>
                    <Grid item xs={12}>
                        <Slider
                            min={0.0}
                            max={5.0}
                            step={0.5}
                            value={[parseFloat(rule.args?.low, 10) || 0.0, parseFloat(rule.args?.high, 10) || 5.0]}
                            onChange={handleReviewRatingSliderChange}
                            valueLabelDisplay="auto"
                        />
                    </Grid>
                </Grid>
            )}

            {rule.tpl === USER_ENTERS_LOCATION_RADIUS && (
                <Grid item xs={12} sm={6} alignItems="flex-start" container spacing={2}>
                    <Grid item>
                        <TextField
                            disabled={disabled}
                            name="latitude"
                            label="Latitude"
                            value={rule.args?.latitude || ''}
                            variant='filled'
                            size="small"
                            margin="none"
                            type='number'
                            fullWidth
                            onChange={handleUserEntersLocationChange}
                            sx={{ maxWidth: 110, marginRight: "1rem" }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            disabled={disabled}
                            name="longitude"
                            value={rule.args?.longitude || ''}
                            variant='filled'
                            size="small"
                            margin="none"
                            label="Longitude"
                            type='number'
                            fullWidth
                            onChange={handleUserEntersLocationChange}
                            sx={{ maxWidth: 110, marginRight: "1rem" }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            disabled={disabled}
                            name="radius"
                            label="Radius (meters)"
                            value={rule.args?.radius || ''}
                            variant='filled'
                            size="small"
                            margin="none"
                            type='number'
                            fullWidth
                            onChange={handleUserEntersLocationChange}
                            sx={{ maxWidth: 110, marginRight: "1rem" }}
                        />
                    </Grid>
                    <Grid item xs={'auto'} mt={0.75}>
                        <RadioGroup disabled={disabled} row name="fetch_method" alignContent="center" alignItems="center" value={rule.args?.fetch_method} onChange={handleUserEntersLocationChange}>
                            <Typography fontWeight={700} mt={1.15} mr={1}>Fetch Method:</Typography>
                            <FormControlLabel value="gps" control={<Radio />} label="GPS" />
                            <FormControlLabel value="ip_address" control={<Radio />} label="IP Address" />
                        </RadioGroup>
                    </Grid>

                </Grid>
            )}

            {rule.tpl === BOOKING_DATETIME_IS_WITHIN_RANGE && (
                <Grid item xs={12} sm={7} alignItems="center" container spacing={2}>
                    <Grid item xs={12} alignItems="center" container direction="row" spacing={2}>
                        <Grid item xs={12} sm={6} container alignItems="center">
                            <TextField
                                disabled={disabled}
                                // helperText={rule.args?.delta ? `Or ${rule.args?.delta} seconds` : ' '}
                                name="delta"
                                value={rule.args?.raw_delta || ''}
                                variant='filled'
                                size="small"
                                margin="none"
                                fullWidth
                                onChange={onBookingDatetimeWithinXTimeChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} container alignItems="center">
                            <FormControl fullWidth size="small" disabled={disabled}>
                                <Select value={rule.args?.unit || 3600} name="unit" variant='filled' label="Unit" onChange={onBookingDatetimeWithinXTimeChange}>
                                    <MenuItem value={1}>seconds</MenuItem>
                                    <MenuItem value={60}>minutes</MenuItem>
                                    <MenuItem value={3600}>hours</MenuItem>
                                    <MenuItem value={86400}>days</MenuItem>
                                </Select>
                                {/* <FormHelperText>Unit</FormHelperText> */}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === USER_HAS_OTHER_TOURS && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <List dense disablePadding>
                            <ListItem dense disablePadding key="same_departure">
                                <ListItemButton disabled={disabled} onClick={(ev) => onUserHasOtherToursUpdate(ev, "same_departure")} dense>
                                    <ListItemIcon>
                                        <Checkbox name="same_departure" edge="start" checked={!!rule.args?.same_departure} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary="Same tour, date & time" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem dense disablePadding key="same_tour">
                                <ListItemButton disabled={disabled} onClick={(ev) => onUserHasOtherToursUpdate(ev, "same_tour")} dense>
                                    <ListItemIcon>
                                        <Checkbox name="same_tour" edge="start" checked={!!rule.args?.same_tour} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary="Same tour" />
                                </ListItemButton>
                            </ListItem>
                            <Divider/>
                            {[['future', 'Future tours'], ['active', 'Active tours'], ['past', 'Past tours']].map(([flag, label]) => (
                                <ListItem dense disablePadding key={flag}
                                    secondaryAction={(
                                        <TextField select size="small" variant="standard" sx={{ width: 110 }}
                                            value={rule.args?.time_ref?.[flag] || 'now'}
                                            disabled={disabled || !rule.args?.time?.[flag]}
                                            onChange={(ev) => onUserHasOtherToursUpdate(ev, "time_ref", [flag, ev.target.value])}
                                        >
                                            {OTHER_TOURS_TIME_REF_OPTIONS.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                >
                                    <ListItemButton disabled={disabled} sx={{ pr: 16 }} onClick={(ev) => onUserHasOtherToursUpdate(ev, "time", flag)} dense>
                                        <ListItemIcon>
                                            <Checkbox name={flag} edge="start" checked={!!rule.args?.time?.[flag]} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={label} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">
                            &quot;Booking start/end&quot; evaluate against the triggering booking (or the
                            order&apos;s first/last booking). A flag whose reference can&apos;t be
                            resolved matches nothing.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={7} container alignItems="flex-start">
                        <RemoteAutocomplete
                            filterSelected
                            fullWidth
                            // disabled={!hasWritePerm}
                            resource="tour"
                            label="Tour"
                            multiple
                            variant="filled"
                            size="small"
                            onChange={(ev, tours) => onUserHasOtherToursUpdate(ev, "tours", tours)}
                            value={rule.args?.tours}
                            getOptionLabel={(opt) => {
                                if (!opt.is_bespoke) {
                                    return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                                }
                                return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                            }}
                            textFieldProps={{
                                helperText: "Leave empty to match any tour — search by name or id:1234"
                            }}
                        />
                        <Box sx={{ mt: 1.5, width: '100%' }}>
                            <TagConditionField
                                resource="notification"
                                hideSources
                                allowSpecChips
                                caption="Also match tours carrying these tags — combined with the tour list above (either side matches)."
                                value={tourTagsToFieldValue(rule.args?.tour_tags)}
                                onChange={(v) => onUserHasOtherToursUpdate({ target: null }, "tour_tags", fieldValueToTourTags(v))}
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === INCLUDE_RULESET && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <RemoteAutocomplete
                            filterSelected
                            fullWidth
                            urlSuffix={null}
                            // disabled={!hasWritePerm}
                            resource="notification_ruleset"
                            label="Ruleset:"
                            variant="filled"
                            size="small"
                            onChange={(ev, ruleset) => onNestedRulesetChange(ev, ruleset)}
                            value={rule.nested_ruleset}
                            getOptionLabel={(opt) => {
                                console.log('opt', opt);
                                return `${opt.name} (ID: ${opt.id})`;
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RulesetPreview id={rule.nested_ruleset?.id}/>
                        {/* { ruleset && (<>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {ruleset.name}&nbsp;<IconButton href={`/notifications/rulesets/${ruleset.id}`} target="_blank"><EditIcon color="primary" fontSize="small"/></IconButton>
                            </Typography>
                            <Typography variant="body1">
                                Match {ruleset.match_type === RULES_MATCH_ANY ? 'any' : 'all'} of the following rules
                            </Typography>
                            <List dense disablePadding>
                                { ruleset.rules.length > 0 ? (<>
                                    {ruleset.rules.map((r, ruleIdx) => {
                                        const label = `${r.negate ? 'NOT ' : ''}${NOTIFICATION_RULE_TEMPLATES[r.tpl]}`;
                                        let SublabelComponent = <NotificationRuleDescription rule={r}/>
                                        return <ListItem dense disablePadding key={ruleIdx}>
                                            <ListItemText primary={label} secondary={SublabelComponent} />
                                        </ListItem>
                                    })}
                                </>) : (<>
                                    <Typography variant="body2">No rules in this ruleset</Typography>
                                </>) }
                            </List>
                        </>)} */}
                    </Grid>
                </Grid>
            )}

            {rule.tpl === EVENT_TYPE && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            multiple
                            disabled={disabled}
                            options={eventOptions}
                            getOptionLabel={(option) => option.label}
                            onChange={onEventTypeChange}
                            value={rule.args?.events || []}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Events"
                                    variant="filled"
                                    size="small"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_DELIVERY_METHOD && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            filterSelectedOptions
                            disabled={disabled}
                            options={deliveryMethodsOptions}
                            getOptionLabel={(option) => option.name}
                            onChange={onDeliveryMethodsChange}
                            value={rule.args?.delivery_methods || []}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Delivery methods"
                                    variant="filled"
                                    size="small"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            disabled={disabled}
                            control={<Checkbox
                                disabled={disabled}
                                edge="start"
                                checked={!!rule.args?.allow_undefined}
                                onChange={onAllowUndefinedDeliveryMethodChange}
                                tabIndex={-1}
                                disableRipple
                                />}
                            label="Ignore this rule if delivery method is unknown"
                        />
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_CHECKIN_SOURCE && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl disabled={disabled} fullWidth variant="filled" size="small">
                            <InputLabel>Check-in registration source</InputLabel>
                            <Select value={rule.args?.sources || []} name="checkin_source" multiple onChange={onCheckinSourceSelectSwitch} label="Check-in registration source">
                                { checkinSourceMenuItems }
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_TRIP_TIME_RANGE && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} container spacing={2} direction="row">
                        <Grid item sm={2} xs={4}>
                            <FormControlLabel
                                disabled={disabled}
                                control={<Checkbox
                                    checked={!!rule.args?.future}
                                    name="future"
                                    color="primary"
                                    onChange={onTripTimeRangeSwitchChange}
                                    />}
                                label="Future" />
                        </Grid>
                        <Grid item>
                            <TextField
                                disabled={disabled || !rule.args?.future}
                                name="future_delta"
                                label="Delta (minutes)"
                                variant="filled"
                                size="small"
                                value={Math.round((rule.args?.future_delta || 0)/60)}
                                onChange={onTripTimeRangeDeltaChange}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} container spacing={2} direction="row">
                        <Grid item sm={2} xs={4}>
                            <FormControlLabel
                                disabled={disabled}
                                control={<Checkbox
                                    checked={!!rule.args?.present}
                                    name="present"
                                    color="primary"
                                    onChange={onTripTimeRangeSwitchChange}
                                    />}
                                label="Present" />
                        </Grid>
                        <Grid item>
                            <TextField
                                disabled={disabled || !rule.args?.present}
                                name="low_delta"
                                label="Low delta (minutes)"
                                variant="filled"
                                size="small"
                                value={Math.round((rule.args?.low_delta || 0)/60)}
                                onChange={onTripTimeRangeDeltaChange}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                disabled={disabled || !rule.args?.present}
                                name="high_delta"
                                label="High delta (minutes)"
                                variant="filled"
                                size="small"
                                value={Math.round((rule.args?.high_delta || 0)/60)}
                                onChange={onTripTimeRangeDeltaChange}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} container spacing={2} direction="row">
                        <Grid item sm={2} xs={4}>
                            <FormControlLabel
                                disabled={disabled}
                                control={<Checkbox
                                    checked={!!rule.args?.past}
                                    name="past"
                                    color="primary"
                                    onChange={onTripTimeRangeSwitchChange}
                                    />}
                                label="Past" />
                        </Grid>
                        <Grid item>
                            <TextField
                                disabled={disabled || !rule.args?.past}
                                name="past_delta"
                                label="Delta (minutes)"
                                variant="filled"
                                size="small"
                                value={Math.round((rule.args?.past_delta || 0)/60)}
                                onChange={onTripTimeRangeDeltaChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_PRECHECKIN_STATUS && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={6}>
                        <FormControl disabled={disabled} fullWidth variant="filled" size="small">
                            <InputLabel>Pre-check-in status</InputLabel>
                            <Select value={rule.args?.precheckin_statuses || []} name="precheckin_statuses" multiple onChange={onPrecheckinStatusSelectSwitch} label="Pre-check-in statuses">
                                { precheckinStatusMenuItems }
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_EMAIL_RECEIPT && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={6}>
                        <FormControlLabel
                            disabled={disabled}
                            control={<Checkbox
                                checked={!!rule.args?.email_receipt}
                                name="email_receipt"
                                color="primary"
                                onChange={onEmailReceiptSelectSwitch}
                                />}
                            label="Booking system permits to send the email receipt to the user" />
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_SETTLEMENT_METHOD && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={6}>
                        <FormControl disabled={disabled} fullWidth variant="filled" size="small">
                            <InputLabel>Settlement method</InputLabel>
                            <Select value={rule.args?.settlement_method || []} name="settlement_method" multiple onChange={onSettlementMethodSelectSwitch} label="Settlement methods">
                                { settlementMethodMenuItems }
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            {rule.tpl === TPL_HAS_SUBBOOKINGS && (
                <Grid item xs={12} sm={7} alignItems="flex-start" container spacing={2}>
                    <Grid item xs={12} sm={9}>
                        <FormControl disabled={disabled} fullWidth variant="filled" size="small">
                            <InputLabel>Applies to</InputLabel>
                            <Select
                                value={rule.args?.scope || 'any'}
                                name="scope"
                                onChange={onHasSubbookingsScopeChange}
                                label="Applies to"
                            >
                                <MenuItem value="current">Current tour participant only</MenuItem>
                                <MenuItem value="any">Any tour participant in the booking</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            { !ruleHasRightPanel && (<Grid item xs={12} sm={7}></Grid>) }

            <Grid item xs={'auto'} sm={1} xl={'auto'} style={{ marginTop: props.isSmallScreen ? '-1.4rem' : '0' }}>
                <FormGroup>
                    <FormControlLabel style={{ paddingTop: '0.4rem' }} control={<Checkbox disabled={disabled} checked={rule.negate} onChange={(ev) => onRuleChange(ev)} name="negate" />} label="Not" />
                </FormGroup>
            </Grid>
            <Grid style={{ transform: `translate(0rem, ${props.isSmallScreen ? '0.4rem' : '0.6rem'})`, marginTop: props.isSmallScreen ? '-1.4rem' : '0' }} component={Box} item xs={6} sm={1} container justifyContent="flex-start">
                <DeleteIconButton title="Delete rule" size="medium" fontSize="medium" disabled={props.disableSaveButton || disabled} onClick={() => props.handleRemoveRule(rule.idx)} />
            </Grid>
            {/* <Grid component={Box} item xs={1} display={{ xs: 'none', sm: 'block' }} container justifyContent="flex-end">
                <IconButton disabled={props.disableSaveButton}>
                    <DeleteIcon onClick={() => props.handleRemoveRule(rule.idx)} style={{ cursor: 'pointer', paddingTop: '0.35rem' }} color="secondary" />
                </IconButton>
            </Grid> */}
        </Grid>
    );
};

export default NotificationRule;
